import { Card } from "@/components/ui/card";
import type { FactorExplanation } from "@/hooks/useLocationForecast";

interface FactorBreakdownProps {
  factors: FactorExplanation[];
  totalScore: number;
  category: string;
  algorithmVersion?: string;
}

/**
 * Get color class based on score value
 */
function getScoreColor(score: number): string {
  if (score >= 85) return "bg-green-500";
  if (score >= 70) return "bg-lime-500";
  if (score >= 50) return "bg-yellow-500";
  if (score >= 30) return "bg-orange-500";
  return "bg-red-500";
}

/**
 * FactorItem - Individual factor display with score bar
 */
function FactorItem({ factor }: { factor: FactorExplanation }) {
  return (
    <div className="space-y-2 py-3">
      {/* Header: Name and contribution */}
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
          {factor.name}
        </span>
        <span className="text-sm font-semibold text-blue-600 dark:text-blue-400">
          +{factor.contribution.toFixed(1)} pts
        </span>
      </div>

      {/* Score bar visualization */}
      <div className="relative h-6 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
        <div
          className={`absolute top-0 left-0 h-full transition-all duration-500 ${getScoreColor(factor.score)}`}
          style={{ width: `${factor.score}%` }}
        />
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-xs font-medium text-gray-900 dark:text-gray-100 mix-blend-difference">
            {factor.score}/100
            {factor.weight > 0 && (
              <span className="ml-1 opacity-75">({factor.weight}% weight)</span>
            )}
          </span>
        </div>
      </div>

      {/* Interpretation */}
      <p className="text-sm text-gray-700 dark:text-gray-300">
        {factor.interpretation}
      </p>

      {/* Technical details */}
      <p className="text-xs text-gray-500 dark:text-gray-400 font-mono">
        {factor.details}
      </p>
    </div>
  );
}

/**
 * FactorBreakdown - Detailed factor-by-factor explanation of prediction score
 */
export function FactorBreakdown({ factors, totalScore, category, algorithmVersion = 'V2.2' }: FactorBreakdownProps) {
  // Defensive check: ensure factors is actually an array before rendering
  if (!factors || !Array.isArray(factors) || factors.length === 0) {
    return null;
  }

  return (
    <Card className="p-4 mt-4 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between pb-2 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Why This Score?
          </h3>
          <div className="text-right">
            <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {totalScore}/100
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400 uppercase tracking-wide">
              {category}
            </div>
          </div>
        </div>

        {/* Factors list */}
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {factors.map((factor, index) => (
            <FactorItem key={`${factor.name}-${index}`} factor={factor} />
          ))}
        </div>

        {/* Methodology note */}
        <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
          <p className="text-xs text-gray-500 dark:text-gray-400 italic text-center">
            Scores calculated using Algorithm {algorithmVersion} with atmospheric physics modeling
          </p>
        </div>
      </div>
    </Card>
  );
}
